import { cs } from '../../lib/currency';
import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { DashboardLayout } from '../../components/DashboardLayout';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Label } from '../../components/ui/label';
import { Badge } from '../../components/ui/badge';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../../components/ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../../components/ui/alert-dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from '../../components/ui/tabs';
import { Checkbox } from '../../components/ui/checkbox';
import { Switch } from '../../components/ui/switch';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../../components/ui/table';
import api from '../../services/api';
import { useFeature } from '../../hooks/useFeature';
import { toast } from 'sonner';
import {
  Plus,
  Edit,
  Trash2,
  Archive,
  Calendar,
  MapPin,
  Users,
  DollarSign,
  Send,
  UserPlus,
  Shield,
  Loader2,
  Mail,
  AlertTriangle,
  Copy,
} from 'lucide-react';
import { AdminTournamentFormDialog } from '../../components/tournaments/AdminTournamentFormDialog';
import TournamentAmountChangeConfirmDialog from '../../components/admin/TournamentAmountChangeConfirmDialog';
import ImageLightbox from '../../components/shared/ImageLightbox';
import {
  statusColors,
  registrationStatusColors,
} from '../../components/tournaments/constants';
import { useBusinessTimezone, getTodayInTz } from '../../utils/businessTime';

const AdminTournaments = () => {
  const businessTz = useBusinessTimezone();
  // iter317 — Plan-gating demo. `enabled` reflects whether the tenant's
  // plan includes tournaments. When false we still RENDER the button
  // (so admins see the surface and know to upgrade) but the click
  // pivots to the upgrade modal instead of opening the create dialog.
  const tournamentsFeature = useFeature('tournaments.registrations');
  const [tournaments, setTournaments] = useState([]);
  const [includeArchived, setIncludeArchived] = useState(false);
  const [loading, setLoading] = useState(true);
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [showEditDialog, setShowEditDialog] = useState(false);
  const [selectedTournament, setSelectedTournament] = useState(null);
  const [activeTab, setActiveTab] = useState('active');

  // iter240: payment_amount change cascade — preview + confirm flow.
  const [amountChangePreview, setAmountChangePreview] = useState(null);
  const [amountChangeOpen, setAmountChangeOpen] = useState(false);
  const [amountChangeSubmitting, setAmountChangeSubmitting] = useState(false);

  // Delete confirmation dialog state
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [tournamentToDelete, setTournamentToDelete] = useState(null);
  const [deleting, setDeleting] = useState(false);
  
  // Team delete confirmation
  const [deleteTeamDialog, setDeleteTeamDialog] = useState(false);
  const [teamToDelete, setTeamToDelete] = useState(null);
  const [teamDeleteWarning, setTeamDeleteWarning] = useState(null);
  const [deletingTeam, setDeletingTeam] = useState(false);
  
  // Add Enrolled Players state
  const [showAddPlayersDialog, setShowAddPlayersDialog] = useState(false);
  const [enrolledPlayers, setEnrolledPlayers] = useState([]);
  const [selectedEnrolledPlayerIds, setSelectedEnrolledPlayerIds] = useState([]);
  const [addPlayersCategory, setAddPlayersCategory] = useState('');
  const [notifyParents, setNotifyParents] = useState(true);
  const [addingPlayers, setAddingPlayers] = useState(false);
  const [loadingEnrolledPlayers, setLoadingEnrolledPlayers] = useState(false);
  
  // Delete Registration state
  const [deleteRegistrationDialog, setDeleteRegistrationDialog] = useState(false);
  const [registrationToDelete, setRegistrationToDelete] = useState(null);
  const [registrationDetails, setRegistrationDetails] = useState(null);
  const [loadingRegistrationDetails, _setLoadingRegistrationDetails] = useState(false);
  const [deletingRegistration, setDeletingRegistration] = useState(false);
  const [refundCheckbox, setRefundCheckbox] = useState(false);
  
  // Programs for notification targeting
  const [programs, setPrograms] = useState([]);
  
  // Image upload state
  const [imageFile, setImageFile] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);
  const [_uploadingImage, setUploadingImage] = useState(false);
  
  const [formData, setFormData] = useState({
    name: '',
    year: parseInt(getTodayInTz('America/New_York').slice(0, 4), 10),
    start_date: '',
    end_date: '',
    details: '',
    location_type: 'local',
    location: '',  // Actual location (e.g., Virginia, NJ, MD, Philly)
    tournament_style: 'competition',
    age_categories: [],
    registration_start_date: '',
    registration_end_date: '',
    initial_payment_amount: 0,
    age_cutoff_date: '',  // Default will be Sept 1 of tournament year
    age_validation_method: 'auto_cutoff',  // 'auto_cutoff' or 'date_range'
    age_category_date_ranges: [],  // For date_range method
    target_program_ids: [],  // Programs to notify
    is_division_based: false,
    divisions: [],
    allow_play_up: true,
    payment_required_for_registration: false,
  });

  // Default NAYCA-style date ranges template
  const _DEFAULT_DATE_RANGES = [
    { category: 'u11', boys_born_after: '', boys_born_before: '', girls_born_after: '', girls_born_before: '' },
    { category: 'u13', boys_born_after: '', boys_born_before: '', girls_born_after: '', girls_born_before: '' },
    { category: 'u15', boys_born_after: '', boys_born_before: '', girls_born_after: '', girls_born_before: '' },
    { category: 'u17', boys_born_after: '', boys_born_before: '', girls_born_after: '', girls_born_before: '' },
  ];

  const [_expenseForm, _setExpenseForm] = useState({
    category: '',
    description: '',
    amount: 0,
    age_category: '',  // Optional: expense for specific age category
  });

  useEffect(() => {
    fetchTournaments();
    fetchPrograms();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [includeArchived]);

  const fetchTournaments = async () => {
    try {
      const url = includeArchived ? '/tournaments?include_archived=true' : '/tournaments';
      const res = await api.get(url);
      // Sort tournaments by start_date (earliest first)
      const sortedTournaments = [...res.data].sort((a, b) => 
        new Date(a.start_date) - new Date(b.start_date)
      );
      setTournaments(sortedTournaments);
    } catch (error) {
      toast.error('Failed to load tournaments');
    } finally {
      setLoading(false);
    }
  };

  const fetchPrograms = async () => {
    try {
      const res = await api.get('/programs');
      setPrograms(res.data);
    } catch (error) {
      console.error('Failed to load programs');
    }
  };

  const fetchEnrolledPlayersForTournament = async (tournamentId, category = null) => {
    setLoadingEnrolledPlayers(true);
    try {
      let url = `/tournaments/${tournamentId}/available-players`;
      if (category) {
        url += `?category=${category}`;
      }
      const res = await api.get(url);
      setEnrolledPlayers(res.data);
    } catch (error) {
      toast.error('Failed to load enrolled players');
      setEnrolledPlayers([]);
    } finally {
      setLoadingEnrolledPlayers(false);
    }
  };

  const handleOpenAddPlayersDialog = async (tournament) => {
    setSelectedTournament(tournament);
    setSelectedEnrolledPlayerIds([]);
    setAddPlayersCategory(tournament.age_categories?.[0] || '');
    setNotifyParents(true);
    setShowAddPlayersDialog(true);
    await fetchEnrolledPlayersForTournament(tournament.id, tournament.age_categories?.[0]);
  };

  const handleAddEnrolledPlayers = async () => {
    if (selectedEnrolledPlayerIds.length === 0) {
      toast.error('Please select at least one player');
      return;
    }
    if (!addPlayersCategory) {
      toast.error('Please select an age category');
      return;
    }

    setAddingPlayers(true);
    try {
      const res = await api.post(
        `/tournaments/${selectedTournament.id}/add-enrolled-players?category=${addPlayersCategory}&notify_parents=${notifyParents}`,
        selectedEnrolledPlayerIds
      );
      
      const { total_added, notifications_sent, results } = res.data;
      
      if (total_added > 0) {
        toast.success(`${total_added} player(s) added to tournament${notifications_sent > 0 ? `, ${notifications_sent} notification(s) sent` : ''}`);
      }
      
      // Show any errors
      const errors = results.filter(r => r.status !== 'added');
      if (errors.length > 0) {
        errors.forEach(e => toast.warning(`${e.player_name || e.player_id}: ${e.message}`));
      }
      
      setShowAddPlayersDialog(false);
      setSelectedEnrolledPlayerIds([]);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to add players');
    } finally {
      setAddingPlayers(false);
    }
  };

  const toggleEnrolledPlayerSelection = (playerId) => {
    setSelectedEnrolledPlayerIds(prev => 
      prev.includes(playerId) 
        ? prev.filter(id => id !== playerId)
        : [...prev, playerId]
    );
  };

  const selectAllEnrolledPlayers = () => {
    if (selectedEnrolledPlayerIds.length === enrolledPlayers.length) {
      setSelectedEnrolledPlayerIds([]);
    } else {
      setSelectedEnrolledPlayerIds(enrolledPlayers.map(p => p.id));
    }
  };

  const handleCreate = async () => {
    try {
      const res = await api.post('/tournaments', formData);
      const newTournamentId = res.data.id;
      
      // Upload image if selected
      if (imageFile) {
        await uploadTournamentImage(newTournamentId);
      }
      
      toast.success('Tournament created successfully');
      setShowCreateDialog(false);
      resetForm();
      fetchTournaments();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create tournament');
    }
  };

  const handleUpdate = async () => {
    // iter240: detect tournament fee change. If the amount changed AND
    // the tournament has registrations, fetch the preview FIRST so the
    // admin can confirm the refunds / new invoices that will fire.
    const oldAmount = Number(selectedTournament?.initial_payment_amount || 0);
    const newAmount = Number(formData?.initial_payment_amount || 0);
    const amountChanged = Math.abs(oldAmount - newAmount) > 0.005;
    if (amountChanged) {
      try {
        const { data: preview } = await api.post(
          `/tournaments/${selectedTournament.id}/preview-payment-amount-change`,
          { new_amount: newAmount },
        );
        if (preview.total_registrations_affected > 0) {
          setAmountChangePreview(preview);
          setAmountChangeOpen(true);
          return; // Wait for admin to confirm via the dialog.
        }
        // No registrations affected — proceed with the normal PUT.
      } catch (err) {
        toast.error(err?.response?.data?.detail || 'Failed to preview fee change.');
        return;
      }
    }
    await doActualUpdate();
  };

  const doActualUpdate = async () => {
    try {
      await api.put(`/tournaments/${selectedTournament.id}`, formData);

      // Upload new image if selected
      if (imageFile) {
        await uploadTournamentImage(selectedTournament.id);
      }

      toast.success('Tournament updated successfully');
      setShowEditDialog(false);
      resetForm();
      fetchTournaments();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update tournament');
    }
  };

  // iter240: admin clicked "Confirm & apply" in the amount-change dialog.
  // Runs the cascade FIRST, then completes the rest of the form update.
  const handleConfirmAmountChange = async () => {
    if (!selectedTournament || !amountChangePreview) return;
    setAmountChangeSubmitting(true);
    try {
      const newAmount = Number(formData.initial_payment_amount || 0);
      const { data: result } = await api.post(
        `/tournaments/${selectedTournament.id}/apply-payment-amount-change`,
        { new_amount: newAmount },
      );
      const refunds = result.counts?.refund || 0;
      const charges = result.counts?.additional_charge || 0;
      const updates = result.counts?.update_unpaid || 0;
      const errCount = (result.errors || []).length;
      if (errCount > 0) {
        toast.warning(
          `Fee change applied with ${errCount} error(s). Check details.`,
          { duration: 8000 }
        );
      } else {
        toast.success(
          `Fee change applied — ${refunds} refund(s), ${charges} new invoice(s), ${updates} invoice update(s).`,
          { duration: 6000 }
        );
      }
      setAmountChangeOpen(false);
      setAmountChangePreview(null);
      // The amount is now persisted by the apply endpoint. Save the rest of
      // the form via the normal PUT for any other field changes.
      await doActualUpdate();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to apply fee change.');
    } finally {
      setAmountChangeSubmitting(false);
    }
  };
  
  const uploadTournamentImage = async (tournamentId) => {
    try {
      setUploadingImage(true);
      const formDataUpload = new FormData();
      formDataUpload.append('file', imageFile);
      
      await api.post(`/files/upload/tournament-image?tournament_id=${tournamentId}`, formDataUpload, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      toast.success('Tournament image uploaded');
    } catch (error) {
      console.error('Image upload error:', error);
      toast.error('Failed to upload image');
    } finally {
      setUploadingImage(false);
    }
  };
  
  const handleImageSelect = (e) => {
    const file = e.target.files?.[0];
    if (file) {
      // Validate file type
      if (!['image/jpeg', 'image/png', 'image/gif', 'image/webp'].includes(file.type)) {
        toast.error('Please select a valid image file (JPEG, PNG, GIF, or WebP)');
        return;
      }
      // Validate file size (5MB max)
      if (file.size > 5 * 1024 * 1024) {
        toast.error('Image must be less than 5MB');
        return;
      }
      setImageFile(file);
      setImagePreview(URL.createObjectURL(file));
    }
  };
  
  const removeImage = async () => {
    if (showEditDialog && selectedTournament?.image_path) {
      // Delete from server
      try {
        await api.delete(`/files/tournament-image/${selectedTournament.id}`);
        toast.success('Image removed');
        fetchTournaments();
      } catch (error) {
        toast.error('Failed to remove image');
        return;
      }
    }
    setImageFile(null);
    setImagePreview(null);
  };

  const openDeleteConfirmation = (tournament, e) => {
    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }
    setTournamentToDelete(tournament);
    setDeleteConfirmOpen(true);
  };

  const handleConfirmDelete = async () => {
    if (!tournamentToDelete) return;
    
    setDeleting(true);
    try {
      await api.delete(`/tournaments/${tournamentToDelete.id}`);
      toast.success('Tournament deleted successfully');
      fetchTournaments();
      setDeleteConfirmOpen(false);
      setTournamentToDelete(null);
    } catch (error) {
      console.error('Delete error:', error);
      toast.error(error.response?.data?.detail || 'Failed to delete tournament');
    } finally {
      setDeleting(false);
    }
  };

  const handleCopyTournament = async (tournament, e) => {
    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }
    
    try {
      const response = await api.post(`/tournaments/${tournament.id}/copy`);
      toast.success(`Tournament copied! New tournament: ${response.data.name}`);
      fetchTournaments();
    } catch (error) {
      console.error('Copy error:', error);
      toast.error(error.response?.data?.detail || 'Failed to copy tournament');
    }
  };

  // iter306bb: archive / unarchive handler — warns if paid
  // registrations exist but allows the admin to proceed.
  const handleArchiveTournament = async (tournament, e) => {
    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }
    if (tournament.is_archived) {
      try {
        await api.post(`/tournaments/${tournament.id}/unarchive`);
        toast.success(`"${tournament.name}" restored to active tournaments`);
        fetchTournaments();
      } catch (error) {
        toast.error(error.response?.data?.detail || 'Failed to unarchive');
      }
      return;
    }
    // Count paid registrations BEFORE archiving so the confirm message is meaningful.
    let paidCount = 0;
    try {
      const resp = await api.get(`/tournaments/${tournament.id}/registrations`);
      paidCount = (resp.data || []).filter(
        (r) => ['paid', 'completed'].includes(String(r.payment_status || '').toLowerCase()),
      ).length;
    } catch (_) { /* best-effort */ }
    const msg = paidCount > 0
      ? `Archive "${tournament.name}"?\n\n⚠️ This tournament has ${paidCount} paid registration(s). Parents and their payment history will be preserved, but the tournament will be hidden from active lists.`
      : `Archive "${tournament.name}"?\n\nIt will be hidden from active tournament lists. You can restore it anytime via "Include archived".`;
    if (!window.confirm(msg)) return;
    try {
      const r = await api.post(`/tournaments/${tournament.id}/archive`);
      const paid = r.data?.paid_registrations || 0;
      toast.success(
        paid > 0
          ? `Archived "${tournament.name}" (${paid} paid registration${paid === 1 ? '' : 's'} preserved)`
          : `Archived "${tournament.name}"`
      );
      fetchTournaments();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to archive');
    }
  };

  // Registration delete handlers
  const handleDeleteRegistration = async () => {
    if (!registrationToDelete || !selectedTournament) return;
    
    setDeletingRegistration(true);
    try {
      const isPaid = registrationToDelete.payment_status === 'paid';
      const queryParams = new URLSearchParams();
      if (isPaid && refundCheckbox) {
        queryParams.append('refund', 'true');
      }
      
      const url = `/tournaments/${selectedTournament.id}/registrations/${registrationToDelete.id}/admin${queryParams.toString() ? '?' + queryParams.toString() : ''}`;
      const res = await api.delete(url);
      
      const verb = res.data.soft_cancelled ? 'cancelled' : 'deleted';
      if (res.data.refund) {
        toast.success(`Registration ${verb}. Refund of ${cs()}${res.data.refund.amount} processed (${res.data.refund.status})`);
      } else {
        toast.success(`Registration for ${res.data.player_name} ${verb} successfully`);
      }
      
      fetchTournaments();
      setDeleteRegistrationDialog(false);
      setRegistrationToDelete(null);
      setRegistrationDetails(null);
    } catch (error) {
      console.error('Delete registration error:', error);
      toast.error(error.response?.data?.detail || 'Failed to delete registration');
    } finally {
      setDeletingRegistration(false);
    }
  };

  // Team handlers
  const handleDeleteTeam = async () => {
    if (!teamToDelete) return;
    
    setDeletingTeam(true);
    try {
      // Delete with force=true if team has players
      const forceParam = teamDeleteWarning ? '?force=true' : '';
      await api.delete(`/tournaments/${selectedTournament.id}/teams/${teamToDelete}${forceParam}`);
      
      if (teamDeleteWarning) {
        toast.success(`Team deleted. ${teamDeleteWarning.playerCount} player(s) released to open pool.`);
      } else {
        toast.success('Team deleted successfully');
      }
      
      fetchTournaments();
    } catch (error) {
      console.error('Delete team error:', error);
      toast.error(error.response?.data?.detail || 'Failed to delete team');
    } finally {
      setDeletingTeam(false);
      setDeleteTeamDialog(false);
      setTeamToDelete(null);
      setTeamDeleteWarning(null);
    }
  };

  const openEditDialog = (tournament) => {
    setSelectedTournament(tournament);
    setFormData({
      name: tournament.name,
      year: tournament.year,
      start_date: tournament.start_date,
      end_date: tournament.end_date,
      details: tournament.details || '',
      location_type: tournament.location_type,
      location: tournament.location || '',
      tournament_style: tournament.tournament_style || 'competition',
      age_categories: tournament.age_categories,
      registration_start_date: tournament.registration_start_date,
      registration_end_date: tournament.registration_end_date,
      initial_payment_amount: tournament.initial_payment_amount,
      age_cutoff_date: tournament.age_cutoff_date || '',
      age_validation_method: tournament.age_validation_method || 'auto_cutoff',
      age_category_date_ranges: tournament.age_category_date_ranges || [],
      target_program_ids: tournament.target_program_ids || [],
      is_division_based: tournament.is_division_based || false,
      divisions: tournament.divisions || [],
      allow_play_up: tournament.allow_play_up !== false,
      payment_required_for_registration: tournament.payment_required_for_registration === true,
    });
    // Set image preview if tournament has an image
    if (tournament.image_path) {
      setImagePreview(`${(typeof window !== "undefined" && window.location ? window.location.origin : process.env.REACT_APP_BACKEND_URL)}/api/files/tournament-image/${tournament.id}`);
    } else {
      setImagePreview(null);
    }
    setImageFile(null);
    setShowEditDialog(true);
  };

  const resetForm = () => {
    setFormData({
      name: '',
      year: parseInt(getTodayInTz(businessTz).slice(0, 4), 10),
      start_date: '',
      end_date: '',
      details: '',
      location_type: 'local',
      location: '',
      tournament_style: 'competition',
      age_categories: [],
      registration_start_date: '',
      registration_end_date: '',
      initial_payment_amount: 0,
      age_cutoff_date: '',
      age_validation_method: 'auto_cutoff',
      age_category_date_ranges: [],
      target_program_ids: [],
      is_division_based: false,
      divisions: [],
      allow_play_up: true,
      payment_required_for_registration: false,
      notify_parents: false,
    });
    setSelectedTournament(null);
    setImageFile(null);
    setImagePreview(null);
  };

  const toggleAgeCategory = (category) => {
    setFormData(prev => ({
      ...prev,
      age_categories: prev.age_categories.includes(category)
        ? prev.age_categories.filter(c => c !== category)
        : [...prev.age_categories, category]
    }));
  };

  const toggleTargetProgram = (programId) => {
    setFormData(prev => ({
      ...prev,
      target_program_ids: prev.target_program_ids.includes(programId)
        ? prev.target_program_ids.filter(id => id !== programId)
        : [...prev.target_program_ids, programId]
    }));
  };

  const filteredTournaments = tournaments.filter(t => {
    if (activeTab === 'all') return true;
    return t.status === activeTab;
  });

  if (loading) {
    return (
      <DashboardLayout title="Tournaments">
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-4 border-blue-500 border-t-transparent"></div>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout title="Tournaments">
      <div className="space-y-6" data-testid="admin-tournaments">
        {/* Header */}
        <div className="space-y-3">
          <p className="text-slate-600">Manage tournaments and track participation</p>
          <div className="flex flex-wrap items-center gap-3">
            <label className="inline-flex items-center gap-2 px-3 py-1.5 rounded border border-slate-200 bg-white cursor-pointer hover:bg-slate-50">
              <input
                type="checkbox"
                checked={includeArchived}
                onChange={(e) => setIncludeArchived(e.target.checked)}
                className="h-4 w-4"
                data-testid="tournaments-include-archived-toggle"
              />
              <span className="text-sm font-medium text-slate-700">Include archived</span>
            </label>
            <Button
              onClick={() => {
                if (!tournamentsFeature.enabled) {
                  tournamentsFeature.prompt('Creating a tournament');
                  return;
                }
                setShowCreateDialog(true);
              }}
              data-testid="create-tournament-btn"
            >
              <Plus className="h-4 w-4 mr-2" />
              Create Tournament
            </Button>
          </div>
        </div>

        {/* Tabs */}
        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList>
            <TabsTrigger value="all">All ({tournaments.length})</TabsTrigger>
            <TabsTrigger value="active">Active ({tournaments.filter(t => t.status === 'active').length})</TabsTrigger>
            <TabsTrigger value="future">Future ({tournaments.filter(t => t.status === 'future').length})</TabsTrigger>
            <TabsTrigger value="past">Past ({tournaments.filter(t => t.status === 'past').length})</TabsTrigger>
          </TabsList>

          <TabsContent value={activeTab} className="mt-6">
            {filteredTournaments.length === 0 ? (
              <Card>
                <CardContent className="p-6 lg:p-8 text-center text-slate-500">
                  No tournaments found
                </CardContent>
              </Card>
            ) : (
              <div className="grid gap-4">
                {filteredTournaments.map((tournament) => (
                  <Card key={tournament.id} className="hover:shadow-md transition-shadow">
                    <CardContent className="p-4 lg:p-6">
                      <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-3 lg:gap-4">
                        {/* Tournament Thumbnail */}
                        {tournament.image_path && (
                          <div className="flex-shrink-0">
                            <ImageLightbox
                              src={`${(typeof window !== "undefined" && window.location ? window.location.origin : process.env.REACT_APP_BACKEND_URL)}/api/files/tournament-image/${tournament.id}`}
                              alt={tournament.name}
                              className="h-16 w-16 lg:h-20 lg:w-20 rounded-lg object-cover border"
                              testId={`admin-tournament-card-image-${tournament.id}`}
                            />
                          </div>
                        )}
                        <div className="flex-1 space-y-1.5 lg:space-y-2 min-w-0">
                          <div className="flex items-center gap-2 flex-wrap">
                            <Link 
                              to={`/admin/tournaments/${tournament.id}`}
                              className="text-base lg:text-lg font-semibold hover:text-blue-600 hover:underline"
                            >
                              {tournament.name}
                            </Link>
                            <Badge className={`text-xs ${statusColors[tournament.status]}`}>
                              {tournament.status}
                            </Badge>
                          </div>
                          
                          <div className="flex items-center gap-2 flex-wrap">
                            <Badge variant="outline" className="text-xs">
                              {tournament.location_type === 'out_of_state' ? 'Out of State' : 'In State'}
                            </Badge>
                            {tournament.location && (
                              <Badge variant="secondary" className="text-xs bg-slate-100">
                                <MapPin className="h-3 w-3 mr-1" />
                                <span className="truncate max-w-[140px] lg:max-w-none">{tournament.location}</span>
                              </Badge>
                            )}
                            <Badge className={`text-xs ${
                              tournament.tournament_style === 'competition' ? 'bg-purple-100 text-purple-700' :
                              tournament.tournament_style === 'recreational' ? 'bg-teal-100 text-teal-700' :
                              'bg-indigo-100 text-indigo-700'
                            }`}>
                              {tournament.tournament_style === 'competition' ? 'Competition' :
                               tournament.tournament_style === 'recreational' ? 'Recreational' : 'League'}
                            </Badge>
                          </div>
                          
                          <div className="flex flex-wrap gap-3 lg:gap-4 text-xs lg:text-sm text-slate-600">
                            <span className="flex items-center gap-1 whitespace-nowrap">
                              <Calendar className="h-3.5 w-3.5 lg:h-4 lg:w-4 shrink-0" />
                              {tournament.start_date} — {tournament.end_date}
                            </span>
                            <span className="flex items-center gap-1 whitespace-nowrap">
                              <DollarSign className="h-3.5 w-3.5 lg:h-4 lg:w-4 shrink-0" />
                              {cs()}{tournament.initial_payment_amount} initial
                            </span>
                          </div>

                          <div className="flex flex-wrap gap-1">
                            {tournament.age_categories.map(cat => (
                              <Badge key={cat} variant="secondary" className="text-xs">
                                {cat.toUpperCase()}
                                {tournament.registration_counts?.[cat] > 0 && (
                                  <span className="ml-1 bg-blue-500 text-white rounded-full px-1.5 py-0.5 text-[10px] font-semibold">
                                    {tournament.registration_counts[cat]}
                                  </span>
                                )}
                              </Badge>
                            ))}
                          </div>

                          {/* Target Programs */}
                          {tournament.target_programs && tournament.target_programs.length > 0 && (
                            <div className="flex items-center gap-2 text-xs text-blue-600">
                              <Send className="h-3 w-3" />
                              <span>Notifies: {tournament.target_programs.map(p => p.name).join(', ')}</span>
                            </div>
                          )}

                          {/* Division & Play-Up Info */}
                          <div className="flex flex-wrap gap-2">
                            {tournament.is_division_based && (
                              <Badge variant="outline" className="text-xs bg-purple-50 text-purple-700 border-purple-200">
                                <Shield className="h-3 w-3 mr-1" />
                                {tournament.divisions?.length || 0} Divisions
                              </Badge>
                            )}
                            {tournament.allow_play_up && (
                              <Badge variant="outline" className="text-xs bg-green-50 text-green-700 border-green-200">
                                Play-Up Allowed
                              </Badge>
                            )}
                          </div>
                        </div>

                        <div className="flex gap-2">
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => handleOpenAddPlayersDialog(tournament)}
                            className="text-emerald-600 hover:text-emerald-700"
                            data-testid={`add-players-tournament-${tournament.id}`}
                          >
                            <UserPlus className="h-4 w-4 mr-1" />
                            Add Players
                          </Button>
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => openEditDialog(tournament)}
                            data-testid={`edit-tournament-${tournament.id}`}
                          >
                            <Edit className="h-4 w-4" />
                          </Button>
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={(e) => handleCopyTournament(tournament, e)}
                            className="text-blue-600 hover:text-blue-700 hover:bg-blue-50"
                            title="Copy Tournament"
                            data-testid={`copy-tournament-${tournament.id}`}
                          >
                            <Copy className="h-4 w-4" />
                          </Button>
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={(e) => handleArchiveTournament(tournament, e)}
                            className={tournament.is_archived ? "text-emerald-700 hover:bg-emerald-50" : "text-amber-700 hover:bg-amber-50"}
                            title={tournament.is_archived ? "Unarchive Tournament" : "Archive Tournament"}
                            data-testid={`archive-tournament-${tournament.id}`}
                          >
                            <Archive className="h-4 w-4" />
                          </Button>
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={(e) => openDeleteConfirmation(tournament, e)}
                            className="text-red-600 hover:text-red-700 hover:bg-red-50"
                            data-testid={`delete-tournament-${tournament.id}`}
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </TabsContent>
        </Tabs>

        {/* Create/Edit Dialog */}
        <AdminTournamentFormDialog
          showCreateDialog={showCreateDialog}
          setShowCreateDialog={setShowCreateDialog}
          showEditDialog={showEditDialog}
          setShowEditDialog={setShowEditDialog}
          formData={formData}
          setFormData={setFormData}
          imagePreview={imagePreview}
          removeImage={removeImage}
          handleImageSelect={handleImageSelect}
          resetForm={resetForm}
          handleCreate={handleCreate}
          handleUpdate={handleUpdate}
          programs={programs}
          toggleAgeCategory={toggleAgeCategory}
          toggleTargetProgram={toggleTargetProgram}
        />

        {/* Add Enrolled Players Dialog */}
        <Dialog open={showAddPlayersDialog} onOpenChange={setShowAddPlayersDialog}>
          <DialogContent className="max-w-3xl max-h-[85vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <UserPlus className="h-5 w-5 text-emerald-600" />
                Add Enrolled Players to Tournament
              </DialogTitle>
            </DialogHeader>

            {selectedTournament && (
              <div className="space-y-4">
                <div className="bg-slate-50 p-4 rounded-lg">
                  <h4 className="font-medium text-slate-900">{selectedTournament.name}</h4>
                  <p className="text-sm text-slate-500">
                    {selectedTournament.start_date} • {selectedTournament.location_type === 'local' ? 'In State' : 'Out of State'}
                    {selectedTournament.location && ` • ${selectedTournament.location}`}
                  </p>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Age Category</Label>
                    <Select 
                      value={addPlayersCategory} 
                      onValueChange={(value) => {
                        setAddPlayersCategory(value);
                        fetchEnrolledPlayersForTournament(selectedTournament.id, value);
                      }}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Select category" />
                      </SelectTrigger>
                      <SelectContent>
                        {selectedTournament.age_categories?.map(cat => (
                          <SelectItem key={cat} value={cat}>{cat.toUpperCase()}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label>Notify Parents</Label>
                    <div className="flex items-center gap-2 mt-2">
                      <Switch 
                        checked={notifyParents} 
                        onCheckedChange={setNotifyParents}
                        id="notify-parents"
                      />
                      <Label htmlFor="notify-parents" className="font-normal text-sm text-slate-600">
                        Send email notification to parents
                      </Label>
                    </div>
                  </div>
                </div>

                <div className="border rounded-lg">
                  <div className="p-3 bg-slate-50 border-b flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <Checkbox
                        checked={enrolledPlayers.length > 0 && selectedEnrolledPlayerIds.length === enrolledPlayers.length}
                        onCheckedChange={selectAllEnrolledPlayers}
                      />
                      <span className="text-sm font-medium">
                        Available Enrolled Players ({enrolledPlayers.length})
                      </span>
                    </div>
                    {selectedEnrolledPlayerIds.length > 0 && (
                      <Badge className="bg-emerald-100 text-emerald-700">
                        {selectedEnrolledPlayerIds.length} selected
                      </Badge>
                    )}
                  </div>

                  <div className="max-h-[300px] overflow-y-auto">
                    {loadingEnrolledPlayers ? (
                      <div className="p-8 text-center">
                        <Loader2 className="h-6 w-6 animate-spin mx-auto text-slate-400" />
                        <p className="text-sm text-slate-500 mt-2">Loading players...</p>
                      </div>
                    ) : enrolledPlayers.length === 0 ? (
                      <div className="p-8 text-center text-slate-500">
                        <Users className="h-8 w-8 mx-auto mb-2 opacity-50" />
                        <p>No enrolled players available for this category</p>
                        <p className="text-xs mt-1">All enrolled players may already be registered</p>
                      </div>
                    ) : (
                      <Table>
                        <TableHeader>
                          <TableRow>
                            <TableHead className="w-10"></TableHead>
                            <TableHead>Player</TableHead>
                            <TableHead>Age</TableHead>
                            <TableHead>Parent</TableHead>
                            <TableHead>Eligible For</TableHead>
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {enrolledPlayers.map(player => (
                            <TableRow 
                              key={player.id}
                              className={`cursor-pointer ${selectedEnrolledPlayerIds.includes(player.id) ? 'bg-emerald-50' : ''} ${player.is_eligible_for_category === false ? 'opacity-70' : ''}`}
                              onClick={() => toggleEnrolledPlayerSelection(player.id)}
                            >
                              <TableCell>
                                <Checkbox
                                  checked={selectedEnrolledPlayerIds.includes(player.id)}
                                  onCheckedChange={() => toggleEnrolledPlayerSelection(player.id)}
                                />
                              </TableCell>
                              <TableCell>
                                <div className="flex items-center gap-2">
                                  <span className="font-medium">{player.full_name}</span>
                                  {player.is_eligible_for_category === false && (
                                    <Badge variant="outline" className="text-xs bg-amber-50 text-amber-700 border-amber-300">
                                      Age Override
                                    </Badge>
                                  )}
                                </div>
                              </TableCell>
                              <TableCell>{player.age || '-'}</TableCell>
                              <TableCell>
                                <div>
                                  <p className="text-sm">{player.parent_name}</p>
                                  <p className="text-xs text-slate-500">{player.parent_email}</p>
                                </div>
                              </TableCell>
                              <TableCell>
                                <div className="flex flex-wrap gap-1">
                                  {player.eligible_categories?.map(cat => (
                                    <Badge key={cat} variant="outline" className="text-xs">
                                      {cat}
                                    </Badge>
                                  ))}
                                </div>
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    )}
                  </div>
                </div>

                {notifyParents && selectedEnrolledPlayerIds.length > 0 && (
                  <div className="bg-blue-50 p-3 rounded-lg flex items-start gap-2">
                    <Mail className="h-4 w-4 text-blue-600 mt-0.5" />
                    <div className="text-sm text-blue-700">
                      <p className="font-medium">Email notifications will be sent</p>
                      <p className="text-blue-600">{selectedEnrolledPlayerIds.length} parent(s) will be notified about their child's tournament registration.</p>
                    </div>
                  </div>
                )}
              </div>
            )}

            <DialogFooter>
              <Button variant="outline" onClick={() => setShowAddPlayersDialog(false)}>
                Cancel
              </Button>
              <Button 
                onClick={handleAddEnrolledPlayers}
                disabled={selectedEnrolledPlayerIds.length === 0 || addingPlayers}
                className="bg-emerald-600 hover:bg-emerald-700"
                data-testid="confirm-add-players-btn"
              >
                {addingPlayers ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    Adding...
                  </>
                ) : (
                  <>
                    <UserPlus className="h-4 w-4 mr-2" />
                    Add {selectedEnrolledPlayerIds.length} Player(s)
                  </>
                )}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Delete Tournament Confirmation Dialog */}
        <AlertDialog open={deleteConfirmOpen} onOpenChange={setDeleteConfirmOpen}>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>Delete Tournament</AlertDialogTitle>
              <AlertDialogDescription>
                Are you sure you want to delete "{tournamentToDelete?.name}"? 
                This action cannot be undone.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel disabled={deleting}>Cancel</AlertDialogCancel>
              <AlertDialogAction 
                onClick={handleConfirmDelete}
                disabled={deleting}
                className="bg-red-600 hover:bg-red-700"
                data-testid="confirm-delete-tournament-btn"
              >
                {deleting ? 'Deleting...' : 'Delete'}
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>

        {/* Delete Team Confirmation Dialog */}
        <AlertDialog open={deleteTeamDialog} onOpenChange={setDeleteTeamDialog}>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle className="flex items-center gap-2">
                <Trash2 className="h-5 w-5 text-red-600" />
                Delete Team
              </AlertDialogTitle>
              <AlertDialogDescription>
                {teamDeleteWarning ? (
                  <span className="text-amber-600">
                    <strong>Warning:</strong> {teamDeleteWarning.teamName} has {teamDeleteWarning.playerCount} player(s) assigned.
                    If you proceed, these players will be released back to the open pool.
                  </span>
                ) : (
                  'Are you sure you want to delete this team? This action cannot be undone.'
                )}
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel disabled={deletingTeam}>Cancel</AlertDialogCancel>
              <AlertDialogAction 
                onClick={handleDeleteTeam}
                disabled={deletingTeam}
                className="bg-red-600 hover:bg-red-700"
                data-testid="confirm-delete-team-btn"
              >
                {deletingTeam ? 'Deleting...' : 'Delete Team'}
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>

        {/* Delete Registration Confirmation Dialog */}
        <AlertDialog open={deleteRegistrationDialog} onOpenChange={(open) => {
          setDeleteRegistrationDialog(open);
          if (!open) {
            setRegistrationToDelete(null);
            setRegistrationDetails(null);
            setRefundCheckbox(false);
          }
        }}>
          <AlertDialogContent className="max-w-md">
            <AlertDialogHeader>
              <AlertDialogTitle className="flex items-center gap-2">
                <Trash2 className="h-5 w-5 text-red-600" />
                Remove Registration
              </AlertDialogTitle>
              <AlertDialogDescription asChild>
                <div className="space-y-4">
                  {loadingRegistrationDetails ? (
                    <div className="flex items-center justify-center py-4">
                      <Loader2 className="h-6 w-6 animate-spin text-slate-500" />
                    </div>
                  ) : (
                    <>
                      <p>
                        Are you sure you want to remove <strong>{registrationToDelete?.player_name}</strong> from this tournament?
                      </p>
                      
                      {/* Player Details */}
                      <div className="bg-slate-50 rounded-lg p-3 text-sm space-y-1">
                        <div className="flex justify-between">
                          <span className="text-slate-600">Player:</span>
                          <span className="font-medium">{registrationToDelete?.player_name}</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-slate-600">Parent:</span>
                          <span>{registrationToDelete?.parent_name}</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-slate-600">Category:</span>
                          <span>{registrationToDelete?.age_category?.toUpperCase()}</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-slate-600">Status:</span>
                          <Badge className={registrationStatusColors[registrationToDelete?.status] + ' text-xs'}>
                            {registrationToDelete?.status?.replace('_', ' ')}
                          </Badge>
                        </div>
                      </div>
                      
                      {/* Payment Details if paid */}
                      {registrationToDelete?.payment_status === 'paid' && (
                        <div className="border border-green-200 bg-green-50 rounded-lg p-3 space-y-3">
                          <div className="flex items-center gap-2 text-green-700 font-medium">
                            <DollarSign className="h-4 w-4" />
                            Payment Received
                          </div>
                          <div className="text-sm space-y-1">
                            <div className="flex justify-between">
                              <span className="text-slate-600">Amount Paid:</span>
                              <span className="font-semibold text-green-700">{cs()}{registrationToDelete?.payment_amount || registrationDetails?.registration?.payment_amount || 0}</span>
                            </div>
                            {registrationDetails?.payment_transaction && (
                              <>
                                <div className="flex justify-between">
                                  <span className="text-slate-600">Transaction ID:</span>
                                  <span className="text-xs font-mono">{registrationDetails.payment_transaction.id?.substring(0, 8)}...</span>
                                </div>
                                <div className="flex justify-between">
                                  <span className="text-slate-600">Paid At:</span>
                                  <span className="text-xs">{registrationDetails.payment_transaction.updated_at ? new Date(registrationDetails.payment_transaction.updated_at).toLocaleDateString('en-US', { timeZone: businessTz }) : 'N/A'}</span>
                                </div>
                              </>
                            )}
                          </div>
                          
                          {/* Refund Checkbox */}
                          <div className="flex items-center space-x-2 pt-2 border-t border-green-200">
                            <Checkbox
                              id="refund-checkbox"
                              checked={refundCheckbox}
                              onCheckedChange={setRefundCheckbox}
                              data-testid="refund-checkbox"
                            />
                            <label
                              htmlFor="refund-checkbox"
                              className="text-sm font-medium cursor-pointer text-green-700"
                            >
                              Issue full refund via Stripe ({cs()}{registrationToDelete?.payment_amount || 0})
                            </label>
                          </div>
                        </div>
                      )}
                      
                      {/* Warning when paid but no refund selected */}
                      {registrationToDelete?.payment_status === 'paid' && !refundCheckbox && (
                        <div className="border-2 border-red-300 bg-red-50 rounded-lg p-3">
                          <div className="flex items-start gap-2 text-red-700">
                            <AlertTriangle className="h-5 w-5 flex-shrink-0 mt-0.5" />
                            <div>
                              <p className="font-semibold">Warning: No Refund Selected</p>
                              <p className="text-sm mt-1">
                                This player has paid <strong>{cs()}{registrationToDelete?.payment_amount || 0}</strong>. 
                                Deleting without a refund will keep the payment without issuing any reimbursement.
                              </p>
                            </div>
                          </div>
                        </div>
                      )}
                      
                      <p className="text-sm text-amber-600">
                        This will also remove the player from any assigned teams.
                      </p>
                    </>
                  )}
                </div>
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel disabled={deletingRegistration}>Cancel</AlertDialogCancel>
              <AlertDialogAction 
                onClick={handleDeleteRegistration}
                disabled={deletingRegistration || loadingRegistrationDetails}
                className={refundCheckbox ? "bg-amber-600 hover:bg-amber-700" : "bg-red-600 hover:bg-red-700"}
                data-testid="confirm-delete-registration-btn"
              >
                {deletingRegistration ? (
                  <>
                    <Loader2 className="h-4 w-4 animate-spin mr-2" />
                    Processing...
                  </>
                ) : refundCheckbox ? (
                  'Delete & Refund'
                ) : (
                  'Delete Registration'
                )}
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>

        {/* iter240: confirm cascade when tournament fee changes */}
        <TournamentAmountChangeConfirmDialog
          open={amountChangeOpen}
          onOpenChange={(next) => {
            if (amountChangeSubmitting) return;
            setAmountChangeOpen(next);
            if (!next) setAmountChangePreview(null);
          }}
          preview={amountChangePreview}
          onConfirm={handleConfirmAmountChange}
          submitting={amountChangeSubmitting}
        />
      </div>
    </DashboardLayout>
  );
};

export default AdminTournaments;
